import { closeDialog } from 'theme';

/**
 * The desktop menu in sections/header.liquid. Each dropdown is a details element, so it opens with a click, Enter or Space and
 * works before this script loads. This adds: only one dropdown open at a time, and opening on hover for a mouse, with a short
 * delay so a pointer passing over the menu doesn't open everything.
 */
if (!customElements.get('header-menu')) {
  customElements.define(
    'header-menu',
    class extends HTMLElement {
      #hover = window.matchMedia('(hover: hover) and (pointer: fine)');
      #timers = new WeakMap();

      #onToggle = (event) => {
        const details = event.target;
        if (!(details instanceof HTMLDetailsElement) || !details.open) return;
        for (const other of this.querySelectorAll('details[open]')) {
          if (other !== details) other.open = false;
        }
      };

      #schedule(details, open, delay) {
        clearTimeout(this.#timers.get(details));
        this.#timers.set(
          details,
          setTimeout(() => {
            if (open && !details.open) details.dataset.openedByHover = '';
            if (!open) delete details.dataset.openedByHover;
            details.open = open;
          }, delay),
        );
      }

      #onPointer = (event) => {
        if (event.pointerType !== 'mouse' || !this.#hover.matches) return;
        const details = event.target.closest?.('details[data-hover-open]');
        if (!details || !this.contains(details)) return;
        if (event.type === 'pointerover') this.#schedule(details, true, 80);
        else if (!details.contains(event.relatedTarget)) this.#schedule(details, false, 200);
      };

      // A click on a summary the pointer already opened keeps it open, instead of toggling it shut.
      #onClick = (event) => {
        const summary = event.target.closest('summary');
        const details = summary?.parentElement;
        if (details instanceof HTMLDetailsElement && details.open && 'openedByHover' in details.dataset) {
          event.preventDefault();
          delete details.dataset.openedByHover;
        }
      };

      connectedCallback() {
        this.addEventListener('toggle', this.#onToggle, true);
        this.addEventListener('pointerover', this.#onPointer);
        this.addEventListener('pointerout', this.#onPointer);
        this.addEventListener('click', this.#onClick);
      }

      disconnectedCallback() {
        this.removeEventListener('toggle', this.#onToggle, true);
        this.removeEventListener('pointerover', this.#onPointer);
        this.removeEventListener('pointerout', this.#onPointer);
        this.removeEventListener('click', this.#onClick);
      }
    },
  );
}

// The menu drawer is for small screens. If the screen grows past the breakpoint while it's open, it closes.
window.matchMedia('(min-width: 990px)').addEventListener('change', (event) => {
  if (event.matches) document.querySelectorAll('.menu-drawer[open]').forEach(closeDialog);
});

// A sticky header shares its height with the page, so sticky product details can sit just below it.
const stickyHeader = document.querySelector('.header--sticky');
if (stickyHeader) {
  const root = document.documentElement;
  new ResizeObserver(([entry]) => {
    root.style.setProperty('--header-offset', `${Math.round(entry.borderBoxSize[0].blockSize)}px`);
  }).observe(stickyHeader);
}
